import { BASE, axiosCall, MethodType } from "./base";

const METRICS_BASE = `${BASE}metrics/`;
const METRICS_ENTRY = `${METRICS_BASE}v1/event`;
const METRICS_ENTRIES = `${METRICS_BASE}v1/events`;

/**
 * Performs a preflight check and prepares the payload for API calls.
 *
 * @param {Object} payload - The payload for the API request.
 * @returns {Object|undefined} - The modified payload or undefined if preflight fails.
 */
function preflight(payload = {}) {
  if (!BASE) {
    console.error("[API] Base API URL is not set.");
    return;
  }

  if (!process.env.VUE_APP_METRICS_API_KEY) {
    console.error("[API] Metrics API Key is not set.");
    return;
  }

  payload.api_key = process.env.VUE_APP_METRICS_API_KEY;
  payload.use_cache = process.env.NODE_ENV === "production" ? "true" : "false";
  return payload;
}

/**
 * Fetches metrics entries by making an API call.
 *
 * @param {number} page - The page number to fetch.
 * @param {string} id - The unique identifier for filtering entries.
 * @returns {Promise} A promise that resolves with the API response or rejects if the preflight check fails.
 */
function readMetricsEntries(page, id) {
  const payload = preflight({ page, id });

  if (!payload) {
    console.error("[API] Read Metrics Entries could not run.");
    return Promise.reject();
  }

  const params = new URLSearchParams(payload).toString();
  const url = `${METRICS_ENTRIES}?${params}`;
  return axiosCall(MethodType.GET, url, payload);
}

/**
 * Creates a new metrics entry.
 *
 * @param {Object} data - The data for the new metrics entry.
 * @param {string} data.event_name - The name of the event being logged (required).
 * @returns {Promise} A promise that resolves with the API response or rejects if the preflight check fails.
 * @throws Will throw an error if `event_name` is missing from the payload.
 */
function createMetricsEntry(data) {
  const payload = preflight(data);

  if (!payload) {
    console.error("[API] Create Metrics Entry could not run.");
    return Promise.reject();
  }

  if (!payload.event_name) {
    console.error("[API] Payload is missing 'event_name'.");
    return Promise.reject(new Error("Payload is missing 'event_name'."));
  }

  return axiosCall(MethodType.POST, METRICS_ENTRY, payload)
    .then((response) => {
      console.log("Create Metrics Entry Response:", response);
      return response;
    })
    .catch((error) => {
      console.error(
        "Error in Create Metrics Entry:",
        error.response?.data || error
      );
      throw error;
    });
}

export { readMetricsEntries, createMetricsEntry };
